import { Box, Select, TextField, FormControl, Autocomplete, InputLabel, MenuItem, Grid, FormControlLabel, Switch, Checkbox, Tooltip } from "@mui/material";
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FieldValues, UseFormReturn, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import debounce from 'lodash.debounce';
import { getVercelProjects, createProxyApplication } from "../clients/FixieApiClient";
import getErrorMessage from "../utils/getErrorMessage";
import PlanSelector from "../components/PlanSelector";
import { Account, Plan, Region, VercelEnvironment, VercelProject } from "../models";
import { LoadingButton } from "@mui/lab";
import DashboardPage from "../layouts/DashboardPage";
import FormatRegionName from "../components/FormatRegionName";
import trackEvent, { trackConversionEvent } from "../utils/trackEvent";

type ProxyType = 'HTTP' | 'SOCKS';

interface NewProxyApplicationScreenProps {
  plans: Plan[];
  regions: Region[];
  account: Account;
}

interface VercelProjectOption {
  label: string;
  id: string;
  environments: VercelEnvironment[];
}

const EnvironmentCheckbox = (props: { name: string, disabled: boolean, checked: boolean, onChange: (event: ChangeEvent<HTMLInputElement>) => void }) => (
  <FormControlLabel
    key={props.name}
    control={<Checkbox
      onChange={props.onChange}
      checked={props.checked}
      disabled={props.disabled}
    />}
    label={props.name} />
)

const EnvironmentSelector = (props: {
  project: VercelProjectOption,
  selectedEnvironments: string[],
  setSelectedEnvironments: (environments: string[]) => void,
  disabledEnvironments: string[]
}) => (
  <Box>
    {props.project.environments.map(environment => {
      const checked = props.selectedEnvironments.includes(environment.name);
      const disabled = props.disabledEnvironments.includes(environment.name);
      if (disabled) {
        return (
          <Tooltip key={environment.name} title="This environment is unavailable because there is a conflicting environment variable. You may have already added Fixie to this project.">
            <span>
              <EnvironmentCheckbox
                name={environment.name}
                checked={checked}
                disabled={disabled}
                onChange={() => { }}
              />
            </span>
          </Tooltip>
        );
      }
      return <EnvironmentCheckbox
        key={environment.name}
        name={environment.name}
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          if (event.target.checked) {
            props.setSelectedEnvironments([...props.selectedEnvironments, environment.name]);
          } else {
            props.setSelectedEnvironments(props.selectedEnvironments.filter(selectedEnvironment => selectedEnvironment !== environment.name));
          }
        }}
      />
    })}
  </Box>
)

const VercelProjectSelector = (props: { form: UseFormReturn, vercelLinked: boolean, setVercelLinked: (state: boolean) => void, proxyType?: ProxyType }) => {
  const { register, setValue } = props.form;
  const [selectedOption, setSelectedOption] = useState<VercelProjectOption | null>(null);
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);
  const [disabledEnvironments, setDisabledEnvironments] = useState<string[]>([]);
  const [vercelProjects, setVercelProjects] = useState<VercelProjectOption[]>([]);

  const initializeEnvironmentState = useCallback((environments: VercelEnvironment[]) => {
    let availableEnvironments: string[] = [];
    if (props.proxyType) {
      const filterFn = props.proxyType === "HTTP" ?
        (environment: VercelEnvironment) => environment.isAvailable.http === true :
        (environment: VercelEnvironment) => environment.isAvailable.socks === true;
      availableEnvironments = environments.filter(filterFn).map(environment => environment.name)
    } else {
      availableEnvironments = environments.map(environment => environment.name);
    }
    setSelectedEnvironments(availableEnvironments);
    setDisabledEnvironments(environments.filter(environment => !availableEnvironments.includes(environment.name)).map(environment => environment.name));
  }, [setSelectedEnvironments, props.proxyType]);

  const mergeResults = useCallback((results: VercelProjectOption[]) => {
    setVercelProjects((currentProjects) => {
      const newResults = results.filter((result) => {
        return !currentProjects.find((project) => project.id === result.id);
      });
      return [...currentProjects, ...newResults];
    });
  }, []);

  const handleResponse = useCallback(async (response: Response) => {
    const data = await response.json() as VercelProject[];
    const projects = data.map((project) => (
      { label: project.name, id: project.externalId, environments: project.environments }
    ));
    mergeResults(projects);
  }, [mergeResults]);

  useEffect(() => {
    if (selectedOption) {
      initializeEnvironmentState(selectedOption.environments);
    }
  }, [props.proxyType, selectedOption, initializeEnvironmentState]);

  useEffect(() => {
    const fetchVercelProjects = async () => {
      const response = await getVercelProjects();
      if (response.ok) {
        handleResponse(response);
      }
    };
    fetchVercelProjects();
  }, [handleResponse]);

  useEffect(() => {
    setValue('vercelProjectExternalId', selectedOption?.id);
    setValue('name', selectedOption?.label);
    setValue('vercelEnvironments', selectedEnvironments);
  }, [selectedOption, setValue, vercelProjects, selectedEnvironments]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSuggestions = useCallback(
    debounce(async (newInputValue) => {
      if (!newInputValue) {
        return;
      }

      try {
        const response = await getVercelProjects(newInputValue);
        if (response.ok) {
          handleResponse(response);
        } else {
          console.error('Error fetching suggestions:', response);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }, 200), // 200ms debounce time
    []
  );

  const handleInputChange = (event: SyntheticEvent, value: string) => {
    fetchSuggestions(value);
  };

  return (
    <Grid item xs={12}>
      {props.vercelLinked ? (
        <Autocomplete
          options={vercelProjects}
          value={selectedOption}
          onInputChange={handleInputChange}
          onChange={(e, newValue) => {
            setSelectedOption(newValue);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label="Project" />}
        />
      ) : (
        <TextField {...register("name")} required fullWidth label="App name" type="text" variant="outlined" />
      )}
      <Box sx={{ display: 'flex', height: '2rem' }}>
        <Box sx={{ flexGrow: 1 }}>
          <FormControlLabel control={<Switch checked={props.vercelLinked} onChange={(event) => { props.setVercelLinked(event.target.checked) }} />} label="Link to Vercel project" />
        </Box>
        {(props.vercelLinked && selectedOption !== null) && (
          <EnvironmentSelector project={selectedOption} selectedEnvironments={selectedEnvironments} setSelectedEnvironments={setSelectedEnvironments} disabledEnvironments={disabledEnvironments} />
        )}
      </Box>
    </Grid>
  )

}

export default (props: NewProxyApplicationScreenProps) => {
  const form = useForm();
  const { register, handleSubmit, watch } = form;
  const [isLoading, setIsLoading] = useState(false);
  const [vercelLinked, setVercelLinked] = useState(props.account.linkedToVercel);

  const navigate = useNavigate();

  const onSubmit = async (data: FieldValues) => {
    if (!vercelLinked) {
      delete data.vercelProjectExternalId;
      delete data.vercelEnvironments;
    }
    if (vercelLinked && !data.vercelProjectExternalId) {
      toast.error("Please select a Vercel project");
      return;
    }

    setIsLoading(true);
    const response = await createProxyApplication(data);
    if (response.ok) {
      const proxyApplication = await response.json();
      trackConversionEvent('create_proxy_application', proxyApplication);
      toast.success(`Created ${data.name}`);
      navigate("/apps");
    } else if (response.status === 402) {
      const body = await response.json();
      trackEvent('redirect_to_stripe');
      window.location.href = body.checkoutUrl;
    } else {
      toast.error(await getErrorMessage(response));
    }
    setIsLoading(false);
  }

  const watchProxyType = watch("proxyType") as (ProxyType | undefined)
  const planName = register('planName', { required: true })

  return (
    <DashboardPage title="Create Proxy Application">
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ padding: '1rem' }}>
          {props.account.linkedToVercel ? <VercelProjectSelector form={form} vercelLinked={vercelLinked} setVercelLinked={setVercelLinked} proxyType={watchProxyType} /> : (
            <Grid item xs={12}>
              <TextField autoFocus {...register("name")} required fullWidth margin="dense" label="App name" type="text" variant="outlined" />
            </Grid>
          )}
          <Grid item xs={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="select-type-label">Proxy Type</InputLabel>
              <Select {...register("proxyType")} label="Proxy Type" labelId="select-type-label" required fullWidth variant="outlined" defaultValue="">
                <MenuItem value="HTTP">HTTP/HTTPS</MenuItem>
                <MenuItem value="SOCKS">SOCKS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="dense">
              <InputLabel id="select-region-label">Region</InputLabel>
              <Select {...register("regionName")} label="Region" labelId="select-region-label" required fullWidth variant="outlined" defaultValue="">
                {props.regions.map((region: any) => (
                  <MenuItem key={region.name} value={region.name}><FormatRegionName region={region.name} /></MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <PlanSelector type="table" plans={props.plans} inputRef={planName.ref} onChange={planName.onChange} onBlur={planName.onBlur} name={planName.name} proxyType={watchProxyType} labelId="select-plan-label" label="Plan" required variant="outlined" />
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              loading={isLoading}
              disableElevation
              variant="contained"
              type="submit"
              fullWidth
              sx={{ mt: 2, mb: 2, height: "56px" }}
            >
              <span>Next</span>
            </LoadingButton>
          </Grid>
        </Grid>
      </Box >
    </DashboardPage>
  )
};
